"use strict";
/**
 * @license
 * Copyright 2017 Palantir Technologies, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var TypedAction;
(function (TypedAction) {
    /**
     * One of the core functions of Redoodle, `TypedAction.define` creates a Definition
     * to manage all Redux actions of a specific type string, such as `"myapp::set_foo_value"`.
     *
     * Each Definition also associates a payload type `T` for all of its matching actions.
     * For example, the `"myapp::set_foo_value"` Action can associate a required payload shape
     * `{foo: string, value: number}`, which means that all actions in the application
     * with type `"myapp::set_foo_value"` *must* have payloads with a `foo` and a `value`.
     *
     * The syntax for invoking the function is slightly awkward, in favor of more predictable type inferencing.
     * An example invocation is below; note the extra `()` after the payload type declaration in `<{}>`s.
     *
     *
     * ```
     * export const SetFooValueAction = TypedAction.define("myapp::set_foo_value")<{
     *   foo: string;
     *   value: number;
     * }>();
     * ```
     *
     *
     * All Definitions for a Redux-enabled application MUST have unique strings.
     *
     * @param options.validatePayload
     */
    function define(type) {
        return function (options) {
            if (process.env.NODE_ENV !== "production" && options !== undefined && options.validate !== undefined) {
                return new RedoodleDefinitionWithValidator(type, options.validate);
            }
            else {
                return new RedoodleDefinition(type);
            }
        };
    }
    TypedAction.define = define;
    /**
     * Similar to TypedAction.define, creates a NoPayloadDefinition for the given Action type
     * string, like `"example::clear_foo"`. In practice, actions without payloads are
     * usually of the "clear" or "invalidate" variety.
     *
     * The syntax for invoking the function is slightly awkward, in favor of more predictable type inferencing.
     * An example invocation is below; note the extra pair of `()`, for consistency with its sibling `define`
     * function and for better future-compatibility of options.
     *
     *
     * ```
     * export const SetFooValueAction = TypedAction.defineWithoutPayload("myapp::set_foo_value")();
     * ```
     *
     *
     * All Definitions for a Redux-enabled application MUST have unique strings.
     */
    function defineWithoutPayload(type) {
        return function () {
            return new RedoodleNoPayloadDefinition(type);
        };
    }
    TypedAction.defineWithoutPayload = defineWithoutPayload;
    var RedoodleDefinition = (function () {
        function RedoodleDefinition(type) {
            var _this = this;
            this.create = function (payload) {
                return { type: _this.TYPE, payload: payload };
            };
            this.createWithMeta = function (payload, meta) {
                return { type: _this.TYPE, payload: payload, meta: meta };
            };
            this.TYPE = type;
        }
        RedoodleDefinition.prototype.is = function (action) {
            return action.type === this.TYPE;
        };
        return RedoodleDefinition;
    }());
    var RedoodleDefinitionWithValidator = (function () {
        function RedoodleDefinitionWithValidator(type, validator) {
            var _this = this;
            this.create = function (payload) {
                if (!_this.validate(payload)) {
                    throw new Error("'" + _this.TYPE + "' validation failed");
                }
                return { type: _this.TYPE, payload: payload };
            };
            this.createWithMeta = function (payload, meta) {
                if (!_this.validate(payload)) {
                    throw new Error("'" + _this.TYPE + "' validation failed");
                }
                return { type: _this.TYPE, payload: payload, meta: meta };
            };
            this.TYPE = type;
            this.validate = validator;
        }
        RedoodleDefinitionWithValidator.prototype.is = function (action) {
            return action.type === this.TYPE;
        };
        return RedoodleDefinitionWithValidator;
    }());
    var RedoodleNoPayloadDefinition = (function () {
        function RedoodleNoPayloadDefinition(type) {
            var _this = this;
            this.create = function () {
                return { type: _this.type };
            };
            this.createWithMeta = function (meta) {
                return { type: _this.type, meta: meta };
            };
            this.type = type;
        }
        Object.defineProperty(RedoodleNoPayloadDefinition.prototype, "TYPE", {
            get: function () {
                return this.type;
            },
            enumerable: true,
            configurable: true
        });
        RedoodleNoPayloadDefinition.prototype.is = function (action) {
            return action.type === this.type;
        };
        return RedoodleNoPayloadDefinition;
    }());
})(TypedAction = exports.TypedAction || (exports.TypedAction = {}));



//////////////////
// WEBPACK FOOTER
// ./node_modules/redoodle/lib/TypedAction.js
// module id = 109
// module chunks = 0